// Game-mode dropdown for the TopBar.
//
// Reads the initial mode from the Rust side at mount, syncs to a
// local store slot for instant UI feedback, and writes back via
// `set_game_mode` IPC on change. The IPC persists to user.toml and
// triggers a config reload — the next round's pinpoint ensemble
// uses the new prompts immediately, no app restart needed.
//
// Keeps a "manual" badge next to the dropdown when the auto-
// detector hasn't flipped the source flag, so the user can see at
// a glance that what they picked is what's active.

import { useEffect } from "react";
import { getGameMode, setGameMode } from "../ipc/commands";
import {
  GAME_MODE_LABELS,
  GAME_MODE_ORDER,
  type GameMode,
} from "../types/contract";
import { useStore } from "../state/store";

export function GameModeSelector() {
  const mode = useStore((s) => s.gameMode);
  const setMode = useStore((s) => s.setGameMode);
  const showToast = useStore((s) => s.showToast);

  useEffect(() => {
    let cancelled = false;
    getGameMode()
      .then((m) => {
        if (!cancelled) setMode(m);
      })
      .catch((e) => {
        // Non-fatal: dropdown stays on whatever the store
        // initialised with (defaults to classic_location). Logged
        // for dev console; user-facing toast would be noise.
        // eslint-disable-next-line no-console
        console.warn("getGameMode failed:", e);
      });
    return () => {
      cancelled = true;
    };
  }, [setMode]);

  const onChange = async (next: GameMode) => {
    // Optimistic local update so the dropdown feels instant; if the
    // backend write fails, we revert and toast.
    const prev = mode;
    setMode(next);
    try {
      await setGameMode(next);
    } catch (e) {
      setMode(prev);
      showToast(`Couldn't save game mode: ${String(e)}`, "error");
    }
  };

  return (
    <label className="flex flex-col text-[10px] text-fg-muted leading-tight">
      <span>Mode</span>
      <select
        data-testid="game-mode-selector"
        value={mode}
        onChange={(e) => void onChange(e.target.value as GameMode)}
        className="text-xs bg-white/5 border border-white/10 rounded px-1 py-0.5 text-fg"
      >
        {GAME_MODE_ORDER.map((m) => (
          <option key={m} value={m}>
            {GAME_MODE_LABELS[m]}
          </option>
        ))}
      </select>
    </label>
  );
}
